// The arc editor's own rail-pattern frame (EAINT-951): mounts EditorRailShellOrganism with the
// top bar, the head (name + Stages/Flow tabs), the summary strip, the block palette, the stage x
// lane grid or the flow view, and the inspector rail — in that order. `EditorHeaderMolecule` was
// considered for the top bar and for the head and rejected for both: the arc contract splits its
// header into `top` (a breadcrumb whose last crumb is the surface, chips, Save/Discard —
// arc-editor-canvas.md `top`) and `head` (the arc name as a button that opens the arc-level
// inspector, D24, beside the Stages/Flow tabs). Neither row has the molecule's anatomy — back
// button, glyph tile, an `<h1>` title with an action slot — so mounting it would nest a button
// inside an `<h1>` and override its size and border rather than reuse it. Both rows are this
// organism's own markup, matching `SphereTypeComposerOrganism`'s top-bar precedent; the molecule
// is the page-level frame `ArcEditorPage` mounts while the canvas has not loaded.
//
// Source: `arc-app.jsx:265-347` (`sb-top`, `sb-head`, `sb-sum`). The archetype menu (`:286-323`)
// and `PinControl` (`:326`) are waived per the spec's Source Wireframes note. The footer wires
// `PublishGateBarMolecule` under this editor's own `studio.arcEditor.footer.gate` namespace
// (D13: the molecule took a `namespace` prop and an optional `onPreviewSample` so a caller with
// no sample-record concept can reuse it) — `structuralChecks` from the host, `publish = PUT
// isPublished`, per the mapping's `dialog.stages` waiver reason.
//
// The inspector rail is wired to its `idle` state only — every other selection kind
// (stage/transition/intent/action) is gated behind `selection.kind=…`, a non-default state owned
// by Phase 2 (arc-editor-canvas.md:216-433), not this skeleton phase.

import React from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import TagAtom, { type TagTone } from '../../atoms/Tag/TagAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import EditorRailShellOrganism from '../EditorRailShell/EditorRailShellOrganism';
import InspectorRailOrganism from '../InspectorRail/InspectorRailOrganism';
import BlockPaletteMolecule, { type BlockPaletteMoleculeProps } from '../../molecules/BlockPalette/BlockPaletteMolecule';
import ArcLaneGridMolecule, { type ArcLaneGridMoleculeProps } from '../../molecules/ArcLaneGrid/ArcLaneGridMolecule';
import ArcFlowViewMolecule, { type ArcFlowViewMoleculeProps } from '../../molecules/ArcFlowView/ArcFlowViewMolecule';
import ChoiceCardGroupAtom, { type ChoiceCardOption } from '../../atoms/ChoiceCardGroup/ChoiceCardGroupAtom';
import ArcStageInspectorMolecule, {
  type ArcStageInspectorMoleculeProps,
} from '../../molecules/ArcStageInspector/ArcStageInspectorMolecule';
import ArcTransitionInspectorMolecule, {
  type ArcTransitionInspectorMoleculeProps,
} from '../../molecules/ArcTransitionInspector/ArcTransitionInspectorMolecule';
import ArcIntentInspectorMolecule, {
  type ArcIntentInspectorMoleculeProps,
} from '../../molecules/ArcIntentInspector/ArcIntentInspectorMolecule';
import ArcActionInspectorMolecule, {
  type ArcActionInspectorMoleculeProps,
} from '../../molecules/ArcActionInspector/ArcActionInspectorMolecule';
import ArcTemplateInspectorMolecule, {
  type ArcTemplateInspectorMoleculeProps,
} from '../../molecules/ArcTemplateInspector/ArcTemplateInspectorMolecule';
import PublishGateBarMolecule, {
  type PublishGateBarMoleculeProps,
} from '../../molecules/studio/PublishGateBarMolecule';

/** The footer gate-status sentence the Publish button `aria-describedby`s while held. */
const ARC_GATE_STATUS_ID = 'arc-editor-gate-status';
import ConfirmDialogAtom from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import type { AgentThreadParity } from '../composer/AgentThreadOrganism';
import './ArcEditorCanvasOrganism.scss';

export type ArcEditorCanvasView = 'stages' | 'flow';

export interface ArcEditorCanvasSurfaceTag {
  tone: TagTone;
  /** Iconify `lucide:*` name (`AC_TRANS_KINDS`-style glyph, e.g. `pencil`). */
  glyph: string;
  /** Already-localized label (`v3 · published — edits go into a draft`, `authoring: a new arc`, …). */
  label: string;
}

export interface ArcEditorCanvasOrganismProps {
  arcName: string;
  editable: boolean;
  onBack: () => void;
  /**
   * The resolved URL Back leads to (`arc-app.jsx:269-270` — both crumbs are real `href={back}`
   * links). A plain click still goes through `onBack` (which owns the unsaved-changes guard);
   * a modifier or middle click opens the gallery in a new tab without leaving the draft.
   */
  backHref: string;
  backAriaLabel: string;
  /** `top.chip` — surface/version tag (`arc-app.jsx:273-278`). */
  surfaceTag: ArcEditorCanvasSurfaceTag;
  /** `top.chip#2` — omitted when there is nothing unsaved (`arc-app.jsx:279`). */
  unsavedCount?: number;
  /**
   * Save/Discard — not in the prototype's own datums (the interview replaced its "Done" summary
   * dialog with Save + `PublishGateBarMolecule`, per the mapping's waiver reasons), so these
   * render only once there is something to save (`unsavedCount > 0`) and only while editable.
   * Omit both to render neither button (e.g. a host that hasn't wired saving yet).
   */
  onSave?: () => void;
  onDiscard?: () => void;
  /** True while a save request is in flight — disables Save/Discard and swaps the Save label. */
  saving?: boolean;
  view: ArcEditorCanvasView;
  onViewChange: (view: ArcEditorCanvasView) => void;
  viewDisabled?: boolean;
  /** `summary` — stage count, plain text (`arc-app.jsx:342`). */
  stagesCount: number;
  /** `summary` — transition/path count, plain text (`arc-app.jsx:342`). */
  pathsCount: number;
  /** `summary.intents`. */
  intentsCount: number;
  /** `summary` — action count, plain text (`arc-app.jsx:342`). */
  actionsCount: number;
  /** `summary.unreachable` — the warning is omitted at zero, per `arc-app.jsx:343`. */
  unreachableCount: number;
  palette: Omit<BlockPaletteMoleculeProps, 'className'>;
  laneGrid: Omit<ArcLaneGridMoleculeProps, 'className'>;
  flowView: Omit<ArcFlowViewMoleculeProps, 'className'>;
  /**
   * `canvas :: demo=loading` (`arc-app.jsx:429-434`) — the canvas body is fetching the arc's
   * stages/lanes; the lane grid and flow view are not rendered while this is true.
   */
  loading?: boolean;
  /**
   * `canvas :: demo=failed` (`arc-app.jsx:399-403`) — the last save attempt failed. The banner
   * shows the server's own message (per the plan's Waivers: the prototype's example text is
   * demo copy, v1 renders the real error) with a Retry action; the canvas body underneath is
   * still rendered, since the draft is kept.
   */
  saveError?: {
    message: string;
    onRetry: () => void;
  };
  /**
   * `canvas :: demo=new & demo+staged=other` (`arc-app.jsx:525-542`) — a brand-new template has
   * no stages yet. Shows a flat choice group of shape starters plus a single-stage
   * start-from-nothing option. Takes priority over the lane/flow views (there is nothing to lay
   * out yet) but not over `loading`. The prototype's kind/archetype step this sits behind
   * (`arche.label`/`arche.terminalAllowed`, "← Change the kind") is waived — no archetype field
   * exists on `ArcTemplateDefinition` — so every starter is offered directly and always ends on
   * a terminal stage.
   */
  newArc?: {
    starters: Array<{ id: string; label: string; stageCount: number }>;
    onSelectStarter: (id: string) => void;
    onAddSingleStage: () => void;
  };
  /**
   * When present, the inspector rail opens on the selected stage's editor instead of its idle
   * state (`side.selection.kind=stage` — Phase 2 state matrix). The rail's own head (kind eyebrow
   * + title + close) is derived from `stageSelection.name`; `title`/`onClose` are supplied by the
   * host, which owns the selection itself.
   */
  stageSelection?: {
    title: string;
    onClose: () => void;
    body: Omit<ArcStageInspectorMoleculeProps, 'className'>;
  };
  /**
   * When present, the inspector rail opens on the selected transition's editor
   * (`side.selection.kind=transition` — Phase 2 state matrix). Mutually exclusive with
   * `stageSelection`; the host owns which selection kind is active.
   */
  transitionSelection?: {
    title: string;
    onClose: () => void;
    body: Omit<ArcTransitionInspectorMoleculeProps, 'className'>;
  };
  /**
   * When present, the inspector rail opens on the selected intent's editor
   * (`side.selection.kind=intent` — Phase 2 state matrix). Mutually exclusive with
   * `stageSelection`/`transitionSelection`; the host owns which selection kind is active.
   */
  intentSelection?: {
    title: string;
    onClose: () => void;
    body: Omit<ArcIntentInspectorMoleculeProps, 'className'>;
  };
  /**
   * When present, the inspector rail opens on the selected action's editor
   * (`side.selection.kind=action` — Phase 2 state matrix). Mutually exclusive with
   * `stageSelection`/`transitionSelection`/`intentSelection`; the host owns which selection kind
   * is active.
   */
  actionSelection?: {
    title: string;
    onClose: () => void;
    body: Omit<ArcActionInspectorMoleculeProps, 'className'>;
  };
  /**
   * When present, the inspector rail opens on the arc-level editor (D24 — clicking the head's
   * arc name/glyph). Mutually exclusive with every other selection kind; the host owns which
   * selection kind is active. There is no prototype panel for this selection kind — it is an
   * addition beyond the prototype (spec § Scope, design interview "Arc inspector on header
   * select").
   */
  arcSelection?: {
    title: string;
    onClose: () => void;
    body: Omit<ArcTemplateInspectorMoleculeProps, 'className'>;
  };
  /** Fires when the head's arc name/glyph is clicked — the host owns turning this into
   * `arcSelection`, the same way it owns every other selection kind. */
  onSelectArc?: () => void;
  /**
   * The footer's publish-gate bar (`footer::default`). The prototype's own footer — a dry-run
   * "Try" tester — is waived (`arc-editor-canvas.mapping.json` `footer.*`: no backend endpoint
   * says what would run at a stage); the interview replaced it with Save (header) + this bar,
   * `publish = PUT isPublished`, per the mapping's `dialog.stages` waiver reason. Omitted while
   * there is nothing to gate yet (e.g. a brand-new, unsaved arc).
   */
  footer?: Omit<PublishGateBarMoleculeProps, 'namespace' | 'statusId' | 'onPreviewSample'>;
  /**
   * `footer.publish` — fires the confirm-then-`PUT isPublished:true` flow the host owns
   * (`arc-editor-canvas.md` § Primary action and reversal — "Publish gate | Publish"). Omitted
   * alongside `footer` while there is nothing to gate yet.
   */
  onPublish?: () => void;
  publishing?: boolean;
  className?: string;
}

/** Inert — `agentTarget` is never passed, so `InspectorRailOrganism` never renders the Agent tab
 * or reads these ids (Reuse Audit: "no agent props"), but the prop is required regardless of
 * whether the tab is reachable. Mirrors `AspectEditorRailStubOrganism`'s own inert constant. */
const ARC_EDITOR_SIDE_RAIL_AGENT_PARITY: AgentThreadParity = {
  threadContainer: 'side > panel#2 > list#2',
  itemContainer: 'side > panel#2 > list#2 > each(t.interview)',
  jump: 'side.see-it',
  shared: 'side.asked-once-for-both',
  composeContainer: 'side > panel#2 > group',
  input: 'side.tell-it-what-to-change',
  send: 'side.send',
  critiqueCard: 'side.watching-as-you-edit',
  critiqueHeading: 'side.watching-as-you-edit#2',
  critiqueChip: 'side.chip',
  critiqueDismiss: 'side.button#6',
};

/**
 * The arc editor canvas: the frame and every region in its default (nothing-selected) state
 * (Phase 1, EAINT-951). Structure and control kinds only — the four selection-kind inspector
 * bodies (stage/transition/intent/action) are Phase 2 state-matrix work.
 */
const ArcEditorCanvasOrganism: React.FC<ArcEditorCanvasOrganismProps> = ({
  arcName,
  editable,
  onBack,
  backHref,
  backAriaLabel,
  surfaceTag,
  unsavedCount = 0,
  onSave,
  onDiscard,
  saving = false,
  view,
  onViewChange,
  viewDisabled = false,
  stagesCount,
  pathsCount,
  intentsCount,
  actionsCount,
  unreachableCount,
  palette,
  laneGrid,
  flowView,
  loading = false,
  saveError,
  newArc,
  stageSelection,
  transitionSelection,
  intentSelection,
  actionSelection,
  arcSelection,
  onSelectArc,
  footer,
  onPublish,
  publishing = false,
  className = '',
}) => {
  const { t } = useTranslation();
  // An open check holds Publish but does not disable it (ux-craft I2 — the control stays
  // enabled and explains on submit): the host's `onPublish` fires `arc_editor_publish_blocked`
  // and opens the first open check. Pending ghost proposals have no such host path, so they
  // still disable it.
  const publishHeld = !!footer && footer.checks.some((check) => check.state === 'open');
  const publishDisabled = !footer || publishing || (footer.pendingGhostCount ?? 0) > 0;

  const handleCrumbClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onBack();
  };

  const topContent = (
    <div className="arc-editor-canvas__top" data-region="top" data-testid="arc-editor-canvas-top">
      <ButtonAtom
        variant="text"
        shape="icon-square"
        size="small"
        icon="pi pi-arrow-left"
        aria-label={backAriaLabel}
        onClick={onBack}
        data-datum="top.back-to-studio"
        data-kind="button"
        data-testid="arc-editor-canvas-back"
      />
      <nav
        className="arc-editor-canvas__crumbs"
        aria-label={t('studio.arcEditor.top.breadcrumbAriaLabel')}
        data-datum="top.canvas"
        data-kind="breadcrumb"
        data-container="top > breadcrumb:canvas"
      >
        <a href={backHref} onClick={handleCrumbClick} data-datum="top.arcs" data-kind="link">
          {t('studio.arcEditor.top.arcsLink')}
        </a>
        <GlyphAtom glyph="lucide:chevron-right" aria-hidden />
        <a href={backHref} onClick={handleCrumbClick} data-datum="top.arcname" data-kind="link">
          {arcName}
        </a>
        <GlyphAtom glyph="lucide:chevron-right" aria-hidden />
        <strong data-datum="top.canvas#2" data-kind="strong">
          {t('studio.arcEditor.top.canvasCrumb')}
        </strong>
      </nav>
      <TagAtom
        label={surfaceTag.label}
        icon={`lucide:${surfaceTag.glyph}`}
        tone={surfaceTag.tone}
        size="small"
        data-datum="top.chip"
        data-kind="chip"
      />
      {editable && unsavedCount > 0 && (
        <TagAtom
          label={t('studio.arcEditor.top.unsaved', { count: unsavedCount })}
          icon="lucide:circle-dot"
          tone="warn"
          size="small"
          data-datum="top.chip#2"
          data-kind="chip"
        />
      )}
      {editable && unsavedCount > 0 && (onSave || onDiscard) && (
        <div className="arc-editor-canvas__save-actions">
          {onDiscard && (
            <ButtonAtom
              variant="text"
              size="small"
              label={t('studio.arcEditor.top.discard')}
              onClick={onDiscard}
              disabled={saving}
              data-testid="arc-editor-canvas-discard"
              data-kind="button"
            />
          )}
          {onSave && (
            <ButtonAtom
              variant="primary"
              size="small"
              label={saving ? t('studio.arcEditor.top.saving') : t('studio.arcEditor.top.save')}
              onClick={onSave}
              disabled={saving}
              loading={saving}
              data-testid="arc-editor-canvas-save"
              data-kind="button"
            />
          )}
        </div>
      )}
    </div>
  );

  const headContent = (
    <div className="arc-editor-canvas__head" data-region="head" data-testid="arc-editor-canvas-head">
      <button
        type="button"
        className="arc-editor-canvas__head-name"
        data-container="head > group"
        onClick={onSelectArc}
        disabled={!onSelectArc}
        aria-label={t('studio.arcEditor.head.editArcAria')}
      >
        <GlyphAtom glyph="lucide:shuffle" aria-hidden />
        <strong data-datum="head.arcname" data-kind="strong">
          {arcName}
        </strong>
      </button>
      <SegmentedControlAtom
        asTabs
        mode="single"
        size="small"
        ariaLabel={t('studio.arcEditor.head.viewAriaLabel')}
        value={view}
        onChange={(next) => onViewChange(next as ArcEditorCanvasView)}
        options={[
          { value: 'stages', label: t('studio.arcEditor.head.viewStages'), disabled: viewDisabled },
          { value: 'flow', label: t('studio.arcEditor.head.viewFlow'), disabled: viewDisabled },
        ]}
        data-datum="head.tabs"
        data-kind="tabs"
      />
    </div>
  );

  const summaryContent = (
    <div className="arc-editor-canvas__summary" data-region="summary" data-testid="arc-editor-canvas-summary">
      <span className="arc-editor-canvas__summary-overview">
        {t('studio.arcEditor.summary.stagesCount', { count: stagesCount })}
        {' · '}
        {t('studio.arcEditor.summary.pathsCount', { count: pathsCount })}
        {' · '}
        <strong data-datum="summary.intents" data-kind="strong">
          {t('studio.arcEditor.summary.intents', { count: intentsCount })}
        </strong>
        {' · '}
        {t('studio.arcEditor.summary.actionsCount', { count: actionsCount })}
      </span>
      {unreachableCount > 0 && (
        <span data-datum="summary.unreachable" data-kind="status-mark" data-testid="arc-editor-canvas-unreachable">
          <GlyphAtom glyph="lucide:alert-triangle" aria-hidden />
          {t('studio.arcEditor.summary.unreachable', { count: unreachableCount })}
        </span>
      )}
    </div>
  );

  const idleBody = (
    <div data-datum="side.panel" data-kind="panel">
      <div data-datum="side.nothing-selected" data-kind="callout">
        <EmptyStateAtom
          icon="lucide:mouse-pointer-click"
          title={t('studio.arcEditor.inspector.idleTitle')}
          description={t('studio.arcEditor.inspector.idleDescription')}
          data-testid="arc-editor-canvas-idle"
        />
      </div>
    </div>
  );

  return (
    <>
      {/* appendTo="self": PrimeReact defaults ConfirmDialog to a document.body portal, which
          would escape this subtree and lose the composer's own descendant styles (same
          reasoning as SphereTypeComposerOrganism). */}
      <ConfirmDialogAtom appendTo="self" />
      <EditorRailShellOrganism
        className={`arc-editor-canvas ${className}`.trim()}
        locked={!editable}
        documentRegion="canvas"
        // The shell's rail wrapper is the `side` region root (it is the element that stretches
        // to the body row and scrolls); the inspector rail's own aside no longer carries it.
        railRegion="side"
        banner={topContent}
        header={(
          <>
            {headContent}
            {summaryContent}
          </>
        )}
        index={editable ? <BlockPaletteMolecule {...palette} /> : undefined}
        rail={
          <InspectorRailOrganism
            locked={!editable}
            namespace="studio.arcEditor.inspector"
            agentNamespace="studio.arcEditor.inspector"
            parity={ARC_EDITOR_SIDE_RAIL_AGENT_PARITY}
            entity={{ kind: 'arc', label: arcName }}
            idle={
              stageSelection || transitionSelection || intentSelection || actionSelection || arcSelection
                ? undefined
                : { body: idleBody }
            }
            selection={
              stageSelection
                ? {
                    kind: t('studio.arcEditor.inspector.stage.kindEyebrow'),
                    title: stageSelection.title,
                    body: <ArcStageInspectorMolecule {...stageSelection.body} />,
                  }
                : transitionSelection
                  ? {
                      kind: t('studio.arcEditor.inspector.transition.kindEyebrow'),
                      title: transitionSelection.title,
                      body: <ArcTransitionInspectorMolecule {...transitionSelection.body} />,
                    }
                  : intentSelection
                    ? {
                        kind: t('studio.arcEditor.inspector.intent.kindEyebrow'),
                        title: intentSelection.title,
                        body: <ArcIntentInspectorMolecule {...intentSelection.body} />,
                      }
                    : actionSelection
                      ? {
                          kind: t('studio.arcEditor.inspector.action.kindEyebrow'),
                          title: actionSelection.title,
                          body: <ArcActionInspectorMolecule {...actionSelection.body} />,
                        }
                      : arcSelection
                        ? {
                            kind: t('studio.arcEditor.inspector.arc.kindEyebrow'),
                            title: arcSelection.title,
                            body: <ArcTemplateInspectorMolecule {...arcSelection.body} />,
                          }
                        : undefined
            }
            onCloseSelection={
              stageSelection?.onClose ??
              transitionSelection?.onClose ??
              intentSelection?.onClose ??
              actionSelection?.onClose ??
              arcSelection?.onClose
            }
          />
        }
        footer={
          footer && (
            <div className="arc-editor-canvas__footer">
              <PublishGateBarMolecule
                namespace="studio.arcEditor.footer.gate"
                statusId={ARC_GATE_STATUS_ID}
                {...footer}
              />
              {/* Save in the header is the canvas's one primary action (spec § Primary action;
                  ux-craft I1) — Publish is the gate bar's own, and the inspectors' Apply only
                  closes the rail. */}
              {onPublish && (
                <ButtonAtom
                  variant="secondary"
                  size="small"
                  className="arc-editor-canvas__publish"
                  label={publishing ? t('studio.arcEditor.footer.gate.publishing') : t('studio.arcEditor.footer.gate.publish')}
                  onClick={onPublish}
                  disabled={publishDisabled}
                  title={publishHeld ? t('studio.arcEditor.footer.gate.publishHeldReason') : undefined}
                  aria-describedby={publishHeld ? ARC_GATE_STATUS_ID : undefined}
                  data-testid="arc-editor-canvas-publish"
                />
              )}
            </div>
          )
        }
      >
        {/* The read-only banner states a verdict, so it waits for one: while `loading` the host
            has not resolved `canEdit` yet (it fails closed to `editable: false` meanwhile), and
            a banner that flashes "read-only" over every load would be a lie. */}
        {!editable && !loading && (
          <MessageAtom
            severity="warn"
            iconClass="pi pi-lock"
            datum="canvas.readonly"
            data-testid="arc-editor-canvas-readonly-banner"
            text={t('studio.arcEditor.readOnlyBanner')}
          />
        )}
        {saveError && (
          <MessageAtom
            severity="error"
            iconClass="pi pi-exclamation-triangle"
            datum="canvas.callout"
            data-testid="arc-editor-canvas-save-error"
            text={
              <span className="arc-editor-canvas__save-error">
                {saveError.message}
                <ButtonAtom
                  variant="text"
                  size="small"
                  label={t('common.retry')}
                  onClick={saveError.onRetry}
                  data-datum="canvas.retry"
                  data-kind="button"
                />
              </span>
            }
          />
        )}
        {loading ? (
          <div
            className="arc-editor-canvas__skeleton"
            role="status"
            aria-busy="true"
            aria-label={t('studio.arcEditor.canvas.loadingAriaLabel')}
            data-testid="arc-editor-canvas-skeleton"
          >
            <div className="arc-editor-canvas__skeleton-heads" data-container="each(ghost-bar-heads)">
              {Array.from({ length: 4 }).map((_, i) =>
                i === 0 ? (
                  <div key="head-0" data-datum="canvas.ghost-bar" data-kind="ghost-bar">
                    <SkeletonAtom height="34px" />
                  </div>
                ) : (
                  <SkeletonAtom key={`head-${i}`} height="34px" />
                ),
              )}
            </div>
            <div className="arc-editor-canvas__skeleton-rows" data-container="each(ghost-bar-rows)">
              {Array.from({ length: 12 }).map((_, i) =>
                i === 0 ? (
                  <div key="row-0" data-datum="canvas.ghost-bar#2" data-kind="ghost-bar">
                    <SkeletonAtom height="70px" />
                  </div>
                ) : (
                  <SkeletonAtom key={`row-${i}`} height="70px" />
                ),
              )}
            </div>
          </div>
        ) : newArc ? (
          <div data-kind="callout:empty" data-testid="arc-editor-canvas-newarc">
            <EmptyStateAtom
              icon="lucide:layout-template"
              title={t('studio.arcEditor.canvas.newArc.title', { arcName })}
              description={t('studio.arcEditor.canvas.newArc.body')}
            >
              <div data-container="choice-group">
                <ChoiceCardGroupAtom
                  ariaLabel={t('studio.arcEditor.canvas.newArc.choicesAriaLabel')}
                  layout="inline"
                  value={null}
                  onChange={(value) => {
                    if (value === '__one-stage__') {
                      newArc.onAddSingleStage();
                    } else {
                      newArc.onSelectStarter(value);
                    }
                  }}
                  data-testid="arc-editor-canvas-newarc-choices"
                  options={[
                    ...newArc.starters.map<ChoiceCardOption>((starter) => ({
                      value: starter.id,
                      label: starter.label,
                      note: t('studio.arcEditor.canvas.newArc.starterNoteFinal', {
                        count: starter.stageCount,
                      }),
                      datum: 'canvas.choice-card#2',
                      labelDatum: 'canvas.s-label',
                    })),
                    {
                      value: '__one-stage__',
                      label: t('studio.arcEditor.canvas.newArc.oneStageLabel'),
                      note: t('studio.arcEditor.canvas.newArc.oneStageNote'),
                      datum: 'canvas.one-stage',
                      labelDatum: 'canvas.one-stage#2',
                    },
                  ]}
                />
              </div>
            </EmptyStateAtom>
          </div>
        ) : view === 'stages' ? (
          <ArcLaneGridMolecule {...laneGrid} editable={laneGrid.editable ?? editable} />
        ) : (
          <ArcFlowViewMolecule {...flowView} editable={flowView.editable ?? editable} />
        )}
      </EditorRailShellOrganism>
    </>
  );
};

ArcEditorCanvasOrganism.displayName = 'ArcEditorCanvasOrganism';

export default ArcEditorCanvasOrganism;
